/*
 * Response volume by calendar month, for the Surveys page.
 *
 * Answers "how many survey responses do we get each month" directly, as a
 * breakdown rather than a period picker the user has to keep changing.
 * CALENDAR months on purpose: consecutive rolling-30-day readings overlap, so
 * they can't be compared month on month.
 *
 * The period control here is LOCAL to this tab — it deliberately does not touch
 * the global Data Selection scope, which drives brand-equity calculation and
 * participates in the server cache key.
 *
 * Counts only. The endpoint returns no participant or answer data, and the CSV
 * must never carry any.
 */

import React, { useMemo, useState } from 'react';
import { Card, Select, DatePicker, Button, Table, Space, Empty, Spin, Typography, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../../hooks/useAuth';
import { useGetMonthlyResponseCountsQuery, useGetSurveyCohortsQuery } from '../../services/nestjs-base-api';
import { ISurveyCohort } from '../../services/nestjs-base-api';
import { getCountryName } from '../../constants/countries';

const { Text } = Typography;

// WHOLE MONTHS, deliberately not the platform's rolling "Last N Days" presets.
// A rolling window cuts the oldest month in half, and the row then shows that
// part-month next to full ones as though they were comparable — with Last 30
// Days, a July total of 36 displayed as 12. Month-on-month comparison is the
// entire point here, so the range has to snap to month boundaries.
type RangeKey = '3m' | '6m' | '12m' | '24m' | 'ytd' | 'all' | 'custom';

const RANGE_OPTIONS: Array<{ value: RangeKey; months: number | null }> = [
  { value: '3m', months: 3 },
  { value: '6m', months: 6 },
  { value: '12m', months: 12 },
  { value: '24m', months: 24 },
];

interface Props {
  countryCodes?: string[];
}

export const ResponseVolumeTab: React.FunctionComponent<Props> = ({ countryCodes = [] }) => {
  const { user } = useAuth();
  const intl = useIntl();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;

  const [range, setRange] = useState<RangeKey>('12m');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [cohortIds, setCohortIds] = useState<number[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const { data: cohorts } = useGetSurveyCohortsQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );

  // Bounds are sent as 'YYYY-MM', never timestamps: the server matches them
  // against the same expression it groups by, so a browser in AEST can't drag
  // a sliver of the previous month in as a misleading part-month row.
  const { fromMonth, toMonth } = useMemo(() => {
    if (range === 'custom') {
      if (!customRange) return { fromMonth: undefined, toMonth: undefined };
      return {
        fromMonth: customRange[0].format('YYYY-MM'),
        toMonth: customRange[1].format('YYYY-MM'),
      };
    }
    if (range === 'all') return { fromMonth: undefined, toMonth: undefined };
    if (range === 'ytd') {
      return { fromMonth: dayjs().startOf('year').format('YYYY-MM'), toMonth: undefined };
    }
    const opt = RANGE_OPTIONS.find((o) => o.value === range);
    if (!opt?.months) return { fromMonth: undefined, toMonth: undefined };
    return {
      // months - 1 because the current (partial) month is one of them.
      fromMonth: dayjs().subtract(opt.months - 1, 'month').format('YYYY-MM'),
      toMonth: undefined,
    };
  }, [range, customRange]);

  const { data, isFetching } = useGetMonthlyResponseCountsQuery(
    {
      jwtToken,
      organisationId,
      fromMonth,
      toMonth,
      cohortIds: cohortIds.length ? cohortIds : undefined,
      countryCodes: selectedCountries.length ? selectedCountries : undefined,
    },
    { skip: !jwtToken || !organisationId },
  );

  const surveys = data?.surveys ?? [];
  const months = data?.months ?? [];

  // Survey names repeat across cohorts and countries, so a bare name can't be
  // told apart. Flat form for the CSV, which has nowhere to put a second line.
  const surveyLabel = (s: { surveyName: string; cohortName: string | null; countryCode: string | null }) =>
    [s.surveyName, s.cohortName, s.countryCode].filter(Boolean).join(' · ');

  // On screen the qualifiers go BENEATH the name as tags — inline they made the
  // columns far wider than the counts they head.
  //
  // The header is a bottom-aligned flex column so the tag row sits on the same
  // baseline in every column regardless of whether the name took one line or
  // two, and the tags never wrap against each other (a long cohort name
  // ellipsises instead, with the full text on hover).
  const surveyHeader = (s: { surveyName: string; cohortName: string | null; countryCode: string | null }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
        lineHeight: 1.3,
      }}
    >
      <div style={{ whiteSpace: 'normal', textAlign: 'right' }}>{s.surveyName}</div>
      {(s.cohortName || s.countryCode) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 4,
            fontWeight: 400,
            maxWidth: '100%',
          }}
        >
          {s.cohortName && (
            <Tag
              title={s.cohortName}
              style={{
                margin: 0,
                fontSize: 11,
                maxWidth: 118,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {s.cohortName}
            </Tag>
          )}
          {s.countryCode && (
            <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
              {s.countryCode}
            </Tag>
          )}
        </div>
      )}
    </div>
  );

  // Wide layout: a column per survey plus a Total, so the monthly figure Bree
  // actually reports on needs no arithmetic.
  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'responseVolume.month', defaultMessage: 'Month' }),
        dataIndex: 'monthLabel',
        key: 'month',
        fixed: 'left' as const,
        width: 190,
        render: (label: string, row: Record<string, unknown>) => (
          <span>
            {label}
            {row.isPartial ? (
              <Tag style={{ marginInlineStart: 8, fontSize: 11 }}>
                {intl.formatMessage({
                  id: 'responseVolume.monthToDate',
                  defaultMessage: 'so far',
                })}
              </Tag>
            ) : null}
          </span>
        ),
      },
      ...surveys.map((s) => ({
        title: surveyHeader(s),
        dataIndex: `survey_${s.surveyId}`,
        key: `survey_${s.surveyId}`,
        align: 'right' as const,
        width: 180,
        onHeaderCell: () => ({ style: { verticalAlign: 'bottom' as const } }),
      })),
      {
        title: intl.formatMessage({ id: 'responseVolume.total', defaultMessage: 'Total' }),
        dataIndex: 'total',
        key: 'total',
        align: 'right' as const,
        width: 110,
        render: (v: number) => <Text strong>{v}</Text>,
      },
      // Soaks up leftover width. Without it the last real column stretches to
      // fill the table, leaving Total marooned far from the figures it totals.
      { title: '', key: 'filler' },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surveys, intl],
  );

  const currentMonth = dayjs().format('YYYY-MM');

  const rows = useMemo(
    () =>
      months.map((m) => {
        const row: Record<string, string | number | boolean> = {
          key: m.month,
          monthLabel: dayjs(`${m.month}-01`).format('MMM YYYY'),
          isPartial: m.month === currentMonth,
          total: m.total,
        };
        surveys.forEach((s) => {
          row[`survey_${s.surveyId}`] =
            m.bySurvey.find((b) => b.surveyId === s.surveyId)?.count ?? 0;
        });
        return row;
      }),
    [months, surveys, currentMonth],
  );

  // Column totals for the summary row — the "how many in this whole period"
  // figure, which otherwise has to be added up by eye.
  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    surveys.forEach((s) => {
      totals[`survey_${s.surveyId}`] = months.reduce(
        (sum, m) => sum + (m.bySurvey.find((b) => b.surveyId === s.surveyId)?.count ?? 0),
        0,
      );
    });
    return totals;
  }, [months, surveys]);

  // Built from what is already on screen — counts only, no participant or
  // answer data anywhere in this file.
  const downloadCsv = () => {
    const header = [
      intl.formatMessage({ id: 'responseVolume.month', defaultMessage: 'Month' }),
      ...surveys.map(surveyLabel),
      intl.formatMessage({ id: 'responseVolume.total', defaultMessage: 'Total' }),
    ];
    // Row values include the isPartial boolean, so accept it here too.
    const escape = (v: string | number | boolean) => {
      const str = String(v ?? '');
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [
      header.map(escape).join(','),
      ...rows.map((r) =>
        [
          // Mark the current month in the file too — otherwise a part-month
          // sits next to full ones with nothing to say so.
          r.isPartial
            ? `${r.monthLabel} (${intl.formatMessage({ id: 'responseVolume.monthToDate', defaultMessage: 'so far' })})`
            : r.monthLabel,
          ...surveys.map((s) => r[`survey_${s.surveyId}`]),
          r.total,
        ]
          .map(escape)
          .join(','),
      ),
      [
        intl.formatMessage({ id: 'responseVolume.allMonths', defaultMessage: 'All months' }),
        ...surveys.map((s) => columnTotals[`survey_${s.surveyId}`] ?? 0),
        data?.total ?? 0,
      ]
        .map(escape)
        .join(','),
    ];

    // Filter context lives in the FILENAME, not as header rows — leading
    // metadata breaks the moment the file is sorted or pasted into Sheets.
    const parts = ['responses-by-month'];
    if (fromMonth) parts.push(fromMonth);
    parts.push(toMonth ?? dayjs().format('YYYY-MM'));
    if (cohortIds.length) parts.push(`${cohortIds.length}-cohorts`);
    if (selectedCountries.length) parts.push(selectedCountries.join('-'));

    // Leading BOM: without it Excel on Windows reads the file as ANSI and the
    // "·" in qualified survey names arrives as "Â·".
    const blob = new Blob(['\ufeff' + lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${parts.join('_')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const cohortOptions = (cohorts ?? []) as ISurveyCohort[];

  return (
    <Card>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          value={range}
          onChange={(v) => setRange(v)}
          style={{ width: 190 }}
          options={[
            ...RANGE_OPTIONS.map((o) => ({
              value: o.value,
              label: intl.formatMessage(
                { id: 'responseVolume.lastNMonths', defaultMessage: 'Last {months} months' },
                { months: o.months },
              ),
            })),
            {
              value: 'ytd' as RangeKey,
              label: intl.formatMessage({
                id: 'responseVolume.thisYear',
                defaultMessage: 'This year',
              }),
            },
            {
              value: 'all' as RangeKey,
              label: intl.formatMessage({ id: 'period.allTime', defaultMessage: 'All Time' }),
            },
            {
              value: 'custom' as RangeKey,
              label: intl.formatMessage({ id: 'period.custom', defaultMessage: 'Custom range' }),
            },
          ]}
        />

        {/* Month pickers, not days — the range snaps to whole months anyway, so
            offering days would imply a precision the table can't show. */}
        {range === 'custom' && (
          <DatePicker.RangePicker
            picker="month"
            value={customRange}
            onChange={(v) => setCustomRange(v as [Dayjs, Dayjs] | null)}
            allowClear={false}
          />
        )}

        {/* Hidden when there's nothing to choose between — one cohort or one
            country is not a filter, it's noise. */}
        {cohortOptions.length > 1 && (
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 200 }}
            placeholder={intl.formatMessage({
              id: 'responseVolume.allCohorts',
              defaultMessage: 'All cohorts',
            })}
            value={cohortIds}
            onChange={setCohortIds}
            options={cohortOptions.map((c) => ({
              value: c.id,
              label: c.displayName || c.name,
            }))}
          />
        )}

        {countryCodes.length > 1 && (
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 200 }}
            placeholder={intl.formatMessage({
              id: 'responseVolume.allCountries',
              defaultMessage: 'All countries',
            })}
            value={selectedCountries}
            onChange={setSelectedCountries}
            options={countryCodes.map((c) => ({
              value: c,
              label: getCountryName(c) || c,
            }))}
          />
        )}

        <Button
          icon={<DownloadOutlined />}
          onClick={downloadCsv}
          disabled={!rows.length || isFetching}
        >
          <FormattedMessage id="responseVolume.downloadCsv" defaultMessage="Download CSV" />
        </Button>
      </Space>

      {isFetching ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          description={intl.formatMessage({
            id: 'responseVolume.empty',
            defaultMessage: 'No responses in this period',
          })}
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>
                    <Text strong>
                      <FormattedMessage
                        id="responseVolume.allMonths"
                        defaultMessage="All months"
                      />
                    </Text>
                  </Table.Summary.Cell>
                  {surveys.map((s, i) => (
                    <Table.Summary.Cell key={s.surveyId} index={i + 1} align="right">
                      <Text strong>{columnTotals[`survey_${s.surveyId}`] ?? 0}</Text>
                    </Table.Summary.Cell>
                  ))}
                  <Table.Summary.Cell index={surveys.length + 1} align="right">
                    <Text strong>{data?.total ?? 0}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={surveys.length + 2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <FormattedMessage
                id="responseVolume.footnote"
                defaultMessage="{total} responses across {months} whole months, plus the current month so far. Excludes deleted and rejected responses, so these match your dashboard."
                values={{
                  total: data?.total ?? 0,
                  months: Math.max(0, rows.length - (rows.some((r) => r.isPartial) ? 1 : 0)),
                }}
              />
            </Text>
          </div>
        </>
      )}
    </Card>
  );
};

export default ResponseVolumeTab;
